import { makeStyles } from '@material-ui/styles'
import { uniq, without } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { serverGroupsDoc } from 'app/docLinks'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { useAppSelector } from 'app/store'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { chameleonActions } from 'core/session/chameleonReducers'
import type Theme from 'core/themes/model'
import {
  listBlueprints,
  listHypervisors,
  listResmgrHosts,
} from 'openstack/components/infrastructure/actions'
import { listAvailabilityZones } from 'openstack/components/infrastructure/availability-zones/actions'
import { availabilityZonesSelector } from 'openstack/components/infrastructure/availability-zones/selectors'
import {
  ResourceUsageIndicators,
  RoleStatusesCell,
  StorageUsageIndicators,
} from 'openstack/components/infrastructure/hosts/HelperComponents'
import {
  hypervisorsSelector,
  resmgrHostsSelector,
  UNASSIGNED_CLUSTER,
} from 'openstack/components/infrastructure/selectors'
import { getMigrationsSince } from 'openstack/components/vms/migrations/actions'
import { toGiB, toMHz } from '../infrastructure/helpers'
import ExpandableHostAggregateMetadataView from '../infrastructure/hosts/ExpandableHostAggregateMetadataView'
import { STRETCHED_CLUSTER_METADATA_KEY } from '../infrastructure/stretched-clusters/constants'
import { stretchedClusterNameByIdSelector } from '../infrastructure/stretched-clusters/selectors'
import { migrateVirtualMachine } from './actions'
import { formatVmNameWithId } from './newModel'
import { stretchedClusterByHostIdSelector } from './selectors'
import { AFFINITY_POLICIES } from './server-groups/PolicyPicklist'

const searchTargets = ['name', 'id', 'readableInterfaces']
export const VALID_MIGRATION_STATES = ['active', 'shutoff', 'stopped', 'paused', 'suspended']
export const VALID_COLD_MIGRATION_STATES = ['shutoff', 'stopped', 'suspended']

export default function MigrateVirtualMachineDialog({ rows: vms, onClose }) {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)
  const [showAllHosts, setShowAllHosts] = useState(false)
  const [showCrossAzHosts, setShowCrossAzHosts] = useState(false)
  const [noMatchingHosts, setNoMatchingHosts] = useState(false)
  const [failedMigrationVms, setFailedMigrationVms] = useState([])
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const isAdminUser = isAdmin()

  const { error, bulkUpdate: migrateFn } = useUpdateAction(migrateVirtualMachine)
  const title = `Migrate ${vms.length > 1 ? 'VMs' : 'VM'}`
  const vmHostIds = useMemo(() => {
    return uniq(vms.map((vm) => vm['OS-EXT-SRV-ATTR:host']))
  }, [vms])
  const defaultParams = {
    hosts: [],
  }
  const { params, getParamsUpdater, updateParams } = useParams(defaultParams)

  const { loading: loadingHosts, reload: reloadHosts } = useListAction(listResmgrHosts, {
    params: {},
  })
  const hosts = useSelectorWithParams(resmgrHostsSelector, {} as any)

  const { loading: loadingHypervisors, reload: reloadHypervisors } = useListAction(
    listHypervisors,
    {
      params: {},
    },
  )
  const hypervisors = useSelectorWithParams(hypervisorsSelector, {} as any)

  const { loading: loadingAzs } = useListAction(listAvailabilityZones)
  // StorageUsageIndicators reads the blueprint cache for the shared/local storage hint.
  useListAction(listBlueprints)
  const availabilityZones = useAppSelector(availabilityZonesSelector)

  const { update: pollMigrations } = useUpdateAction(getMigrationsSince)

  // Get cluster from first VM — in PF9 the cluster name maps to the AZ name
  const hostCluster = vms[0]?.clusterName

  const hasMultipleAzs = useMemo(() => {
    const realAzs = availabilityZones.filter((az) => az.zoneName !== 'internal')
    return realAzs.length > 1
  }, [availabilityZones])

  // Authoritative host-id -> AZ name map built from the os-availability-zone
  // response. We send this value as `availability_zone`, so it must be the name
  // Nova itself reports (what the scheduler matches against) rather than a
  // display name. PF9 cluster name normally equals the AZ name, so clusterName
  // remains a safe fallback when a host is absent from the AZ data.
  const azNameByHostId = useMemo(() => {
    const map: Record<string, string> = {}
    availabilityZones.forEach((az) => {
      Object.keys(az?.hosts || {}).forEach((hostId) => {
        map[hostId] = az.zoneName
      })
    })
    return map
  }, [availabilityZones])

  // Cross-AZ: detected when the selected host belongs to a different cluster
  const { selectedHost, isCrossAz, targetAz } = useMemo(() => {
    const selected = params.hosts[0]
    const crossAz = !!(selected && selected.clusterName !== hostCluster)
    return {
      selectedHost: selected,
      isCrossAz: crossAz,
      targetAz: crossAz ? azNameByHostId[selected.id] || selected.clusterName : undefined,
    }
  }, [params.hosts, hostCluster, azNameByHostId])

  // Cross-cluster (cross-AZ) migration is cold-only this release. A cold
  // migrate cannot run against a live VM, so any selected VM that is not
  // already in a cold-migratable state must be powered off first.
  const crossAzBlockedVms = useMemo(() => {
    if (!isCrossAz) return []
    return vms.filter((vm) => !VALID_COLD_MIGRATION_STATES.includes(vm?.state))
  }, [isCrossAz, vms])
  const crossAzNeedsPowerOff = crossAzBlockedVms.length > 0

  // Dismissal is per-selection: picking a different host is a new context, so
  // the banner should reappear even if it was previously dismissed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only call this when selectedHost changes
  useEffect(() => {
    setBannerDismissed(false)
  }, [selectedHost?.id])

  // Get failure domain mapping for hosts
  const stretchedClusterByHostId = useSelectorWithParams(stretchedClusterByHostIdSelector, {})
  const stretchedClusterNameById = useAppSelector(stretchedClusterNameByIdSelector)

  // Get VM's primary failure domain ID (first ID before the colon)
  const primaryFailureDomainId = vms[0]?.metadata?.[STRETCHED_CLUSTER_METADATA_KEY]?.split(':')?.[0]
  const primaryFailureDomainName = stretchedClusterNameById[primaryFailureDomainId]

  // Check if any of the VMs are part of a soft-affinity group
  const softAffinityVm = useMemo(() => {
    return vms[0]?.serverGroup?.policy === AFFINITY_POLICIES.SOFT_AFFINITY
  }, [vms])

  const hardAntiAffinityVm = useMemo(() => {
    return vms[0]?.serverGroup?.policy === AFFINITY_POLICIES.HARD_ANTI_AFFINITY
  }, [vms])

  const softAntiAffinityVm = useMemo(() => {
    return vms[0]?.serverGroup?.policy === AFFINITY_POLICIES.SOFT_ANTI_AFFINITY
  }, [vms])

  const hypervisorHosts = useMemo(() => {
    if (loadingHosts || loadingHypervisors) {
      return []
    }

    // Filter only hypervisors that are enabled AND up
    const hypervisorIds = hypervisors.flatMap((hypervisor) => {
      if (hypervisor?.status === 'enabled' && hypervisor?.state === 'up') {
        return [hypervisor?.service?.host]
      }
      return []
    })
    const validHypervisorIds = without(vmHostIds, hypervisorIds)

    const filteredHosts = hosts.filter(
      (host) =>
        validHypervisorIds.includes(host?.id) &&
        (showCrossAzHosts ||
          hostCluster === UNASSIGNED_CLUSTER ||
          host?.clusterName === hostCluster),
    )

    // Filter to hosts in the VM's primary failure domain
    const domainFilteredHosts = primaryFailureDomainId
      ? filteredHosts.filter(
          (host) => stretchedClusterByHostId[host?.id]?.id === primaryFailureDomainId,
        )
      : filteredHosts

    // Filter out hosts that have a vms from the same server group
    if ((hardAntiAffinityVm || softAntiAffinityVm) && !showAllHosts) {
      const matchingHosts = domainFilteredHosts?.filter((host) => {
        return host?.vms?.every((vm) => vm?.serverGroup?.name !== vms[0]?.serverGroup?.name)
      })
      setNoMatchingHosts(matchingHosts?.length === 0)
      return matchingHosts
    }

    return domainFilteredHosts
  }, [
    hosts,
    hypervisors,
    loadingHosts,
    loadingHypervisors,
    vmHostIds,
    hostCluster,
    showAllHosts,
    showCrossAzHosts,
    hardAntiAffinityVm,
    softAntiAffinityVm,
    stretchedClusterByHostId,
    primaryFailureDomainId,
    vms[0]?.serverGroup?.name,
  ])

  const handleSubmit = useCallback(async () => {
    // Cross-cluster migration is cold-only; a running VM must be powered off
    // first. Guard here in addition to disabling submit so the request can
    // never be issued for a VM that isn't cold-migratable.
    if (crossAzNeedsPowerOff) {
      return
    }
    setSubmitting(true)
    const vmMigrations = vms.map((vm) => {
      const host = isAdminUser ? params.hosts[0]?.id : null
      // Force a cold migrate for cross-cluster moves (live cross-AZ is not
      // supported through the UI this release); otherwise pick cold vs live
      // from the VM's power state as before.
      const useColdMigrate = isCrossAz || VALID_COLD_MIGRATION_STATES.includes(vm?.state)
      const body = useColdMigrate
        ? {
            migrate: {
              host,
              ...(targetAz && { availability_zone: targetAz }),
            },
          }
        : {
            'os-migrateLive': {
              host,
              block_migration: 'auto',
            },
          }
      return { id: vm.id, body, apiVersion: targetAz ? '2.101' : '2.95' }
    })
    const results = await migrateFn(vmMigrations)
    const allSucceeded = results.every((result) => result?.success)

    pollMigrations()
    if (allSucceeded) {
      // Track the VM IDs to check later to
      // see when migration is successful
      dispatch(
        chameleonActions.updateChameleonState({
          migratedVmIds: vms.map((vm) => vm.id),
        }),
      )
      onClose(true)
    } else {
      const failedIds = results
        .filter((result) => !result?.success)
        .map((result) => result?.params?.id)
      const failedVms = vms.filter((vm) => {
        return failedIds.includes(vm?.id)
      })
      setFailedMigrationVms(failedVms)

      // Track the VM IDs to check later to
      // see when migration is successful
      const successIds = results
        .filter((result) => result?.success)
        .map((result) => result?.params?.id)
      if (successIds.length > 0) {
        dispatch(
          chameleonActions.updateChameleonState({
            migratedVmIds: successIds,
          }),
        )
      }

      if (!failedVms?.length) {
        onClose(true)
      }
    }
    setSubmitting(false)
  }, [
    params,
    vms,
    isAdminUser,
    targetAz,
    isCrossAz,
    crossAzNeedsPowerOff,
    dispatch,
    migrateFn,
    onClose,
    pollMigrations,
  ])

  // Reasons the submit button is disabled. Surfaced as a tooltip on the
  // disabled button so the reason is visible even when the banner above the
  // host table is scrolled out of view.
  const antiAffinityNeedsHost = (softAntiAffinityVm || hardAntiAffinityVm) && !params.hosts?.length
  const submitDisabled = antiAffinityNeedsHost || crossAzNeedsPowerOff
  const submitDisabledMessage = crossAzNeedsPowerOff
    ? `Cross-cluster migration is cold-only and requires ${
        crossAzBlockedVms.length > 1 ? 'these VMs' : 'this VM'
      } to be powered off first.`
    : antiAffinityNeedsHost
      ? 'Select a target host to continue.'
      : undefined

  const hostColumns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
      },
      {
        id: 'id',
        label: 'ID',
        display: false,
      },
      {
        id: 'hostAggregateMetadata',
        label: 'Host Aggregates',
        render: (value, item) => (
          <ExpandableHostAggregateMetadataView itemName={item?.name} value={value} />
        ),
        display: false,
      },
      {
        id: 'roleStatuses',
        label: (
          <TitleWithInfoIcon
            titleText="Role Status"
            tooltipMsg={<div>Shows the application status of all roles assigned to this host</div>}
          />
        ),
        render: (value, item) => <RoleStatusesCell value={value} item={item} />,
        display: false,
      },
      {
        id: 'schedulingStatus',
        label: 'Scheduling',
        display: false,
      },
      {
        id: 'clusterName',
        label: 'Cluster',
      },
      {
        id: 'stretchedCluster',
        label: 'Failure Domain',
        render: (_value, item) => {
          const fd = stretchedClusterByHostId[item?.id]
          if (!fd) return <Text variant="body2">—</Text>
          return fd.name
        },
        display: false,
      },
      {
        id: 'physicalNetworkLabels',
        label: 'Physical Network Label',
        width: 100,
        display: false,
        render: (value) => {
          const physicalNetworkLabels = value || []
          return <Text variant="body2"> {physicalNetworkLabels?.join(', ')}</Text>
        },
      },
      {
        id: 'info.responding',
        label: 'Responding',
        render: (value) => (value === undefined ? 'Unknown' : value ? 'True' : 'False'),
      },
      {
        id: 'readableInterfaces',
        label: 'IP Address',
        render: (value) => value?.map((iface) => <div key={iface}>{iface}</div>),
      },
      {
        id: 'compute',
        label: 'Compute',
        render: (_value, item) => (
          <ResourceUsageIndicators
            usageValueConverter={{ fn: toMHz, units: 'MHz' }}
            usageStats={item?.usage?.compute}
            allocationStats={item?.usage?.computeAllocation}
          />
        ),
      },
      {
        id: 'memory',
        label: 'Memory',
        render: (_value, item) => (
          <ResourceUsageIndicators
            allocationValueConverter={{ fn: toGiB, units: 'GiB' }}
            usageStats={item?.usage?.memory}
            allocationStats={item?.usage?.memoryAllocation}
          />
        ),
      },
      {
        id: 'storage',
        label: 'Storage',
        render: (_value, item) => <StorageUsageIndicators usage={item?.usage} />,
      },
    ],
    [stretchedClusterByHostId],
  )

  return (
    <ModalForm
      title={title}
      onSubmit={handleSubmit}
      disableSubmit={submitDisabled}
      disableSubmitMessage={submitDisabledMessage}
      hideSubmit={!!failedMigrationVms?.length}
      onClose={failedMigrationVms?.length ? () => onClose(true) : onClose}
      closeText={failedMigrationVms?.length ? 'Confirm' : 'Cancel'}
      loading={loadingAzs}
      submitting={submitting}
      error={error}
      submitTitle={title}
      panel="dialog"
      maxWidth={isAdminUser ? 1400 : 600}
      open
    >
      {failedMigrationVms?.length ? (
        <div className={classes.container}>
          <Text variant="body2">
            The following VMs failed to start migrating:{' '}
            <b>{failedMigrationVms.map((vm) => formatVmNameWithId(vm)).join(', ')}</b>
          </Text>
          <Text variant="body2">Please verify that these VMs are migratable and try again.</Text>
        </div>
      ) : (
        <div className={classes.container}>
          <Text variant="body2">
            You are about to migrate the following VMs:{' '}
            <b>{vms.map((vm) => formatVmNameWithId(vm)).join(', ')}</b>
          </Text>
          {isAdminUser ? (
            <>
              <Text variant="body2">
                Select the hypervisor you would like to migrate the selected VMs to
              </Text>
              <Text variant="body2">
                <b>Note:</b> Hypervisors hosting a selected VM will not be displayed in the list
                below.
              </Text>
              {(softAffinityVm || softAntiAffinityVm || hardAntiAffinityVm) && (
                <Alert variant="warning" title="Warning">
                  {softAffinityVm && (
                    <Text variant="body2">
                      This virtual machine is part of a soft affinity group. Moving the VM may break
                      the affinity policy.{' '}
                      <ExternalLink url={serverGroupsDoc}>
                        <strong>More info</strong>
                      </ExternalLink>
                    </Text>
                  )}
                  {(softAntiAffinityVm || hardAntiAffinityVm) && (
                    <Text variant="body2">
                      This virtual machine is part of a {softAntiAffinityVm ? 'soft' : 'hard'}{' '}
                      anti-affinity group.{' '}
                      {!noMatchingHosts
                        ? 'Below is a filtered list of hypervisors that the virtual machine could migrate to without breaking the policy.'
                        : `There are no suitable hypervisors in this cluster that this VM can migrate to without breaking the policy. ${
                            softAntiAffinityVm
                              ? 'If you still want to migrate this VM, then click on Show all hosts and select a host from the list where you want to migrate this VM.'
                              : ''
                          }`}
                      <ExternalLink url={serverGroupsDoc}>
                        {' '}
                        <strong>More info</strong>
                      </ExternalLink>
                    </Text>
                  )}
                </Alert>
              )}
              {primaryFailureDomainId && (
                <Alert variant="warning">
                  <Text variant="body2">
                    This virtual machine is part of a failure domain{' '}
                    <strong>{primaryFailureDomainName}</strong>.{' '}
                    {!noMatchingHosts
                      ? 'Below is a filtered list of hypervisors that the virtual machine could migrate to without breaking the policy.'
                      : 'There are no suitable hypervisors in this cluster that this VM can migrate to without breaking the policy.'}{' '}
                    To migrate this VM to another host, first perform{' '}
                    <strong>Change Failure Domain</strong> on the VM, and then proceed with the
                    migration.
                  </Text>
                </Alert>
              )}
              <ListTableField
                id="hosts"
                data={hypervisorHosts}
                loading={loadingHosts || loadingHypervisors}
                columns={hostColumns}
                onChange={getParamsUpdater('hosts')}
                value={params.hosts}
                uniqueIdentifier="id"
                searchTargets={searchTargets}
                onReload={() => {
                  reloadHosts(true)
                  reloadHypervisors(true)
                }}
                emptyText={
                  softAntiAffinityVm || hardAntiAffinityVm
                    ? `This virtual machine is part of a ${
                        softAntiAffinityVm ? 'soft' : 'hard'
                      } anti-affinity group. There are no suitable hypervisors in this cluster that this VM can migrate to without breaking the policy`
                    : 'No available hypervisors found'
                }
                extraToolbarContent={
                  <>
                    {softAntiAffinityVm && (
                      <ToggleSwitch
                        active={showAllHosts}
                        onClick={() => setShowAllHosts(!showAllHosts)}
                        label="Show all hosts"
                        data-testid="show-all-hosts-toggle-switch"
                        className={classes.toggleSwitch}
                      />
                    )}
                    {/* If the VM does not have a Failure Domain and If Multiple AZs are available show this toggle */}
                    {!primaryFailureDomainId && hasMultipleAzs && (
                      <ToggleSwitch
                        active={showCrossAzHosts}
                        onClick={() => {
                          setShowCrossAzHosts(!showCrossAzHosts)
                          updateParams({ hosts: [] })
                        }}
                        label="Show hosts from other clusters"
                        data-testid="show-cross-az-hosts-toggle"
                        className={classes.toggleSwitch}
                      />
                    )}
                  </>
                }
                required
              />
              {isCrossAz && !bannerDismissed && (
                <div className={classes.stickyBanner}>
                  <button
                    type="button"
                    aria-label="Dismiss"
                    className={classes.bannerClose}
                    onClick={() => setBannerDismissed(true)}
                    data-testid="dismiss-cross-cluster-banner"
                  >
                    <FontAwesomeIcon>times</FontAwesomeIcon>
                  </button>
                  <Alert
                    variant={crossAzNeedsPowerOff ? 'error' : 'warning'}
                    title="Cross-cluster migration"
                  >
                    {crossAzNeedsPowerOff ? (
                      <Text variant="body2">
                        Cross-cluster migration is performed as a cold migration and requires the VM
                        to be powered off first. Please stop{' '}
                        <b>{crossAzBlockedVms.map((vm) => formatVmNameWithId(vm)).join(', ')}</b>{' '}
                        and try again.
                      </Text>
                    ) : (
                      <>
                        <Text variant="body2">
                          The selected hypervisor <b>{selectedHost?.name || selectedHost?.id}</b> is
                          in a PCD cluster (<b>{targetAz}</b>), so this VM will be cold-migrated
                          across clusters.
                        </Text>
                        <Text variant="body2">
                          The scheduler will reject the migration if the target cluster cannot
                          provide what the VM requires. Before migrating, check that the target
                          host:
                        </Text>
                        <ul className={classes.warningList}>
                          <li>
                            <Text variant="body2">
                              has a compatible CPU model and any devices the VM needs (for example
                              GPUs or PCI passthrough); and
                            </Text>
                          </li>
                          <li>
                            <Text variant="body2">
                              matches the flavor and image properties enforced by the host
                              aggregates of the target cluster (for example extra specs, image
                              traits, or tenant isolation).
                            </Text>
                          </li>
                        </ul>
                      </>
                    )}
                  </Alert>
                </div>
              )}
            </>
          ) : (
            <Text variant="body2">A new hypervisor will be selected automatically.</Text>
          )}
        </div>
      )}
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: 24,
  },
  toggleSwitch: {
    margin: theme.spacing(0, 2),
  },
  stickyBanner: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    // Opaque background so the scrolling host rows do not bleed through the
    // Alert's translucent tint.
    backgroundColor: theme.components.card.background,
    boxShadow: '0 -4px 8px -4px rgba(0, 0, 0, 0.15)',
  },
  bannerClose: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
    padding: theme.spacing(0.5),
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    lineHeight: 1,
  },
  warningList: {
    margin: 0,
    paddingLeft: theme.spacing(2.5),
    display: 'grid',
    gap: 4,
  },
}))
